import * as vp from 'vopay';
import { createContext, JSX, ReactNode, useContext, useState } from 'react';

export type PermissionsCtx = {
  permissions: vp.ApiClientPermissions;
  setPermissions: (permissions: { [key: string]: keyof vp.ApiClientPermissions } | undefined) => void;
  reset: () => void;
};

// Single source of truth for every permission key the client recognises.
// All default to false; the provider flips the ones returned by the API to true.
const PERMISSION_DEFAULTS: vp.ApiClientPermissions = {
  ViewSubaccounts: false,
  AddSubaccounts: false,
  CreatePayments: false,
  CreateCollections: false,
  ViewBeneficiaries: false,
  AddBeneficiaries: false,
  ViewBulkFileUpload: false,
  CreateBulkFileUpload: false,
  ViewScheduledTransactions: false,
  ViewTransactionHistory: false,
  ViewSettings: false,
  AddSettings: false,
  ViewUsers: false,
  AddUsers: false,
  ManageUserPermissions: false,
  ViewUserRoles: false,
  ManageUserRoles: false,
  CreateAccountTransfer: false,
  ViewClientAccounts: false,
  AddClientAccounts: false,
  CreateClientAccountTransfers: false,
  CreateConversions: false,
  ManageDefaultPayments: false,
  CreateSupportTickets: false,
  ViewSupportTickets: false,
  ViewReconciliation: false,
  ViewAccountingBillsAndInvoices: false,
  ProcessAccountingBillsAndInvoices: false,
  TransactionLabelOverrideEnabled: false,
  TransactionCancellation: false,
  ApproveTransactions: false,
  ViewTransactionApprovalWorkflow: false,
  CreateTransactionApprovalWorkflow: false,
  ViewTransactionReport: false,
  CreateTransactionReport: false,
  ManageTransactionGroups: false,
  ManageEmailDomains: false,
  ManageInterestAccounts: false,
  ViewInterestAccounts: false,
  ViewElinxSettings: false,
  ManageElinxSettings: false,
  CreateBills: false,
  CreateInvoices: false,
  ViewBankAccountTransactionHistory: false,
  ManageDisputes: false,
  ManageBlockedAccounts: false,
  ViewAccountSummary: false,
  ViewAccountandBilling: false,
  ManageAccountandBilling: false,
  CreateFundMyAccountTransactions: false,
  CreateWithdrawMyAccountTransactions: false,
  CreateBankAccountTransfer: false,
  ViewOperationalAccounts: false,
  CreateAutoBalanceTransfer: false,
  CreateElinxConnect: false,
  CreateElinxPay: false,
  CreateElinxCollect: false,
  CreateElinxFlex: false,
  ViewClientAccountNotes: false,
  CreateClientAccountNotes: false,
  ViewLearningCentre: false,
  CreateScheduledTransactions: false,
};

export const PermissionsContext = createContext<PermissionsCtx>({
  permissions: PERMISSION_DEFAULTS,
  setPermissions: () => {},
  reset: () => {},
});

export const PermissionsProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [storedPermissions, setStoredPermissions] = useState<
    { [key: string]: keyof vp.ApiClientPermissions } | undefined
  >();

  const mapPermissions = (userPermissionsList: vp.PermissionsApiResponse | undefined) => {
    if (!userPermissionsList) return PERMISSION_DEFAULTS;

    return Object.values(userPermissionsList).reduce(
      (acc, perm) => {
        acc[perm] = true;
        return acc;
      },
      { ...PERMISSION_DEFAULTS },
    );
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions: mapPermissions(storedPermissions),
        setPermissions: setStoredPermissions,
        reset: () => setStoredPermissions(undefined),
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsCtx => useContext(PermissionsContext);
